/**
 * V5 报告 raw payload → 前端 view model。
 *
 * **后端权威 wrapper 形状**（docs/archive/v5/M5-integration-contract.md §API Contract，
 * 2026-05-20 backend handoff 锁定）：
 *
 *   HTTP body:
 *     {
 *       status: 'done' | 'generating' | 'failed',
 *       report_version: 5,
 *       is_fallback: bool,           // top-level mirror of meta.is_fallback
 *       user_report_v5: { ... } | null
 *     }
 *
 *   user_report_v5:
 *     - meta:     {report_version, assessment_id, depth, depth_label,
 *                  answered_count, total_questions, generated_at, model, is_fallback}
 *     - user:     {nickname, position, industry, ai_usage_purpose}
 *     - overall:  {average, l_grade, l_grade_label, percentage, absolute_grade,
 *                  strongest_ability, weakest_ability}  // 后两者 {code,name,l_grade,l_grade_label,percentage}|null
 *     - abilities[5]: {code, name, average, l_grade, l_grade_label, percentage}
 *     - match: {manager_level, is_match, gaps[], weighted_score, divergence} | null
 *     - 5 LLM 字段：headline_tag / overall_summary / strength_abilities[2] /
 *                  improvement_abilities[2] / priority_actions /
 *                  comprehensive_interpretation / try_first_scenarios /
 *                  be_cautious_scenarios / priority_improvements
 *     - quality:  {is_fallback, validator_failures: string[]}
 *
 * Adapter 行为：
 *  - 把上述结构平摊成 view model（见 createEmptyViewModel）。
 *  - 缺字段 → 给空值/fallback；不抛异常（防御性）。
 *  - 后端如果 ability 已自带 name / l_grade_label，前端不查 constants；
 *    只在缺 name（旧报告 / mock 不完整）时退回到 reportV5Labels.js。
 *  - detailed.unlocked 在 V5 wrapper 里不存在；保留 view model 里的字段为
 *    UI 占位（永远 false），等付费报告单独 milestone 接入。
 */
import {
  ABILITY_LABELS,
  ABILITY_ORDER,
  L_GRADE_LABELS,
  DEPTH_VERSION_LABELS,
} from '../constants/reportV5Labels';

// ---- helpers ---------------------------------------------------------------
function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
}

const FALLBACK_USER_VALUES = new Set(['未填写', '匿名测评者', '']);
function cleanUserValue(v) {
  if (!v || FALLBACK_USER_VALUES.has(v)) return '';
  return v;
}

function abilityName(code, raw) {
  return raw?.name || ABILITY_LABELS[code]?.name || code;
}
function abilityLGradeLabel(item) {
  return item?.l_grade_label || L_GRADE_LABELS[item?.l_grade] || '';
}
// overall.strongest_ability / weakest_ability → 紧凑 view model（缺失 lenient 返回 null）
function overallAbility(raw) {
  if (!raw || typeof raw !== 'object' || !raw.code) return null;
  return {
    code: raw.code,
    name: abilityName(raw.code, raw),
    l_grade: raw.l_grade || '',
    l_grade_label: abilityLGradeLabel(raw),
    percentage: Number.isFinite(raw.percentage) ? raw.percentage : null,
  };
}
function depthVersionLabel(depth, label) {
  return label || DEPTH_VERSION_LABELS[depth] || depth || '基础版';
}
// ---- empty / fallback view model ------------------------------------------
function createEmptyViewModel() {
  return {
    meta: {
      nickname: '匿名测评者',
      position: '',
      industry: '',
      ai_usage_purpose: '',
      version_label: '',
      answers_done: 0,
      answers_total: 0,
      generated_at: '',
      model: '',
    },
    overall: {
      l_grade: '',
      l_grade_label: '',
      percentage: 0,
      absolute_grade: '',
      summary: '',
      headline_tag: '',
      strongest: null,
      weakest: null,
    },
    match: null,
    abilities: [],
    strength_abilities: [],
    improvement_abilities: [],
    priority_actions: [],
    key_findings: [],
    comprehensive_interpretation: '',
    try_first_scenarios: [],
    be_cautious_scenarios: [],
    priority_improvements: [],
    is_fallback: false,
    validator_failures: [],
    detailed: { unlocked: false },
    analysis_access: {
      level: 'ability_only',
      full_explanations_unlocked: false,
      unlock_condition: 'assessment_completed',
    },
    retest_journey: {
      state: 'open',
      baseline_assessment_id: null,
      retest_assessment_id: null,
      learning_completed: false,
      can_start_retest: false,
    },
    report_actions: {
      policy: 'learning_optional',
      learning_allowed: true,
      direct_retest_allowed: true,
    },
    learning_platform: { available: false, homepage_url: '' },
    retest_comparison: { available: false, status: 'retest_not_completed' },
  };
}

// ---- main -----------------------------------------------------------------
/**
 * 把后端 HTTP body 转成稳定的 view model。
 *
 * @param {object} body  - 整个 HTTP response body（顶层有 status / report_version /
 *                          is_fallback / user_report_v5）。也接受直接传 user_report_v5
 *                          内部对象（adapter 自动检测）。
 * @returns {object} view model
 */
export function toReportV5ViewModel(body) {
  const vm = createEmptyViewModel();
  if (!body || typeof body !== 'object') return vm;

  // 兼容两种入参：完整 HTTP body 或 user_report_v5 内部对象
  const inner = body.user_report_v5 && typeof body.user_report_v5 === 'object'
    ? body.user_report_v5
    : body;
  if (!inner || typeof inner !== 'object') return vm;

  const meta = inner.meta || {};
  const user = inner.user || {};
  const overall = inner.overall || {};
  const abilitiesRaw = Array.isArray(inner.abilities) ? inner.abilities : [];
  // inner.traits intentionally ignored — report chain is traits-free (Task 11)
  const quality = inner.quality || {};

  // --- meta + user ---
  vm.meta = {
    nickname: cleanUserValue(user.nickname) || '匿名测评者',
    position: cleanUserValue(user.position),
    industry: cleanUserValue(user.industry),
    ai_usage_purpose: cleanUserValue(user.ai_usage_purpose),
    version_label: depthVersionLabel(meta.depth, meta.depth_label),
    answers_done: Number(meta.answered_count) || 0,
    answers_total: Number(meta.total_questions) || 0,
    generated_at: formatDate(meta.generated_at),
    model: meta.model || '',
  };

  // --- overall ---
  vm.overall = {
    l_grade: overall.l_grade || '',
    l_grade_label: overall.l_grade_label || L_GRADE_LABELS[overall.l_grade] || '',
    percentage: Number.isFinite(overall.percentage) ? overall.percentage : 0,
    absolute_grade: overall.absolute_grade || '',
    summary: inner.overall_summary || '',
    headline_tag: inner.headline_tag || '',
    // 方案A 收口：最强 / 最弱力点名（缺失 → null，前端 lenient 不渲染该行）
    strongest: overallAbility(overall.strongest_ability),
    weakest: overallAbility(overall.weakest_ability),
  };

  // --- match（匹配度，manager_level 为空时后端给 null）---
  vm.match = inner.match || null;

  // --- abilities (canonical sort) ---
  const abilityByCode = new Map(abilitiesRaw.map((a) => [a?.code, a]));
  vm.abilities = ABILITY_ORDER.map((code) => {
    const a = abilityByCode.get(code) || {};
    return {
      code,
      name: abilityName(code, a),
      percentage: Number.isFinite(a.percentage) ? a.percentage : 0,
      l_grade: a.l_grade || '',
      l_grade_label: abilityLGradeLabel(a),
    };
  });

  // --- strength / improvement summaries (各 2 个) ---
  vm.strength_abilities = (inner.strength_abilities || [])
    .slice(0, 2)
    .map((code) => ({ code, name: abilityName(code, abilityByCode.get(code)) }));
  vm.improvement_abilities = (inner.improvement_abilities || [])
    .slice(0, 2)
    .map((code) => ({ code, name: abilityName(code, abilityByCode.get(code)) }));

  // --- LLM lists ---
  vm.priority_actions = (inner.priority_actions || []).slice(0, 3);
  // key_findings：Page 1 右栏「关键发现」3 条（2026-06-04 新增）。旧报告无此字段
  // → 空数组，前端卡片不渲染（lenient）。
  vm.key_findings = (inner.key_findings || []).slice(0, 3);
  vm.comprehensive_interpretation = inner.comprehensive_interpretation || '';
  vm.try_first_scenarios = (inner.try_first_scenarios || []).slice(0, 6);
  vm.be_cautious_scenarios = (inner.be_cautious_scenarios || []).slice(0, 6);
  vm.priority_improvements = (inner.priority_improvements || []).slice(0, 3);

  // --- fallback flag —— 2026-05-21 拆分：粗略 banner 只看 rough，详细 banner 看 detailed ---
  // body.is_fallback 后端语义已收窄到只反映粗略；quality.is_fallback / meta.is_fallback 同源。
  vm.is_fallback = Boolean(
    body.is_fallback ?? quality.is_fallback ?? meta.is_fallback,
  );
  // detailed fallback：顶层新增字段（后端 2026-05-21+），降级回 quality.detailed_is_fallback。
  vm.is_detailed_fallback = Boolean(
    body.is_detailed_fallback ?? quality.detailed_is_fallback ?? false,
  );
  vm.validator_failures = Array.isArray(quality.validator_failures)
    ? quality.validator_failures.slice()
    : [];

  // detailed unlock 从顶层 body.is_detailed_unlocked 读（backend 2026-05-20 14:30 ack 后落地）。
  // 粗略 endpoint 总是返回这个字段；为 true 时粗略报告底部 CTA 显示"查看详细报告"，否则"解锁详细报告"。
  vm.detailed = { unlocked: Boolean(body?.is_detailed_unlocked) };

  const analysisAccess = body?.analysis_access;
  vm.analysis_access = {
    level: analysisAccess?.level === 'full' ? 'full' : 'ability_only',
    full_explanations_unlocked: Boolean(analysisAccess?.full_explanations_unlocked),
    unlock_condition: analysisAccess?.unlock_condition || 'assessment_completed',
  };

  const retestJourney = body?.retest_journey;
  vm.retest_journey = {
    state: ['open', 'learning_completed', 'retest_in_progress', 'closed']
      .includes(retestJourney?.state)
      ? retestJourney.state
      : 'open',
    baseline_assessment_id: retestJourney?.baseline_assessment_id ?? null,
    retest_assessment_id: retestJourney?.retest_assessment_id ?? null,
    learning_completed: Boolean(retestJourney?.learning_completed),
    can_start_retest: Boolean(retestJourney?.can_start_retest),
  };

  const reportActions = body?.report_actions;
  const actionPolicy = [
    'assessment_only',
    'learning_required',
    'learning_optional',
  ].includes(reportActions?.policy)
    ? reportActions.policy
    : 'learning_optional';
  const actionDefaults = {
    assessment_only: { learning: false, directRetest: true },
    learning_required: { learning: true, directRetest: false },
    learning_optional: { learning: true, directRetest: true },
  }[actionPolicy];
  vm.report_actions = {
    policy: actionPolicy,
    learning_allowed: typeof reportActions?.learning_allowed === 'boolean'
      ? reportActions.learning_allowed
      : actionDefaults.learning,
    direct_retest_allowed: typeof reportActions?.direct_retest_allowed === 'boolean'
      ? reportActions.direct_retest_allowed
      : actionDefaults.directRetest,
  };

  const learningPlatform = body?.learning_platform;
  const learningHomepage = typeof learningPlatform?.homepage_url === 'string'
    ? learningPlatform.homepage_url.trim()
    : '';
  vm.learning_platform = {
    available: Boolean(
      learningPlatform?.available && /^https?:\/\//i.test(learningHomepage),
    ),
    homepage_url: /^https?:\/\//i.test(learningHomepage) ? learningHomepage : '',
    ...(learningPlatform?.task_bound === true ? { task_bound: true } : {}),
  };

  const comparison = body?.retest_comparison;
  vm.retest_comparison = comparison && typeof comparison === 'object'
    ? comparison
    : { available: false, status: 'retest_not_completed' };

  return vm;
}

// 供单测 / Storybook 使用
export const __internal = { createEmptyViewModel, formatDate, cleanUserValue };
